/* İmzalı büyütme adresleri — hırsızlığa karşı 1. katman (plan §5.3).

   Büyütme katmanı herkese açık fotoğraf alan adında durmaz. Katman
   açılırken sayfa kısa ömürlü bir imza ister; Worker imzayı kenarda
   doğrular (veritabanına gitmez, mikrosaniyeler sürer).

   Sonuç: kopyalanan bir görsel adresi iki dakika içinde ölür, başka bir
   siteden çerçeveleme (hotlink) imkânsızdır, kazıyıcı bot ancak
   JavaScript çalıştırıp oturum tutarsa bir şey elde eder.

   Yalnızca Web Crypto kullanır — Workers'ta da Node'da da aynı çalışır. */

import { MEDIA_TOKEN_TTL_SECONDS } from './media.ts';

const enc = new TextEncoder();

export type MediaVariant = 'full' | 'mobile';

export type TokenClaims = {
  photoId: string;
  variant: MediaVariant;
  /** Unix saniye. */
  exp: number;
  /** Oturuma bağlama — adres başka bir ziyaretçiye verilse de çalışmaz. */
  sid?: string | undefined;
};

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function unb64url(s: string): Uint8Array<ArrayBuffer> {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function keyFor(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify'
  ]);
}

/** İmzalanan metin. Alanlar ':' ile ayrılır; hiçbiri ':' içeremez. */
function payload(c: TokenClaims): string {
  return `${c.photoId}:${c.variant}:${c.exp}:${c.sid ?? ''}`;
}

/**
 * Bir kare için imza üretir. Dönen dize adres sorgusuna `t=` olarak eklenir.
 * `exp` gövdeye gömülüdür ki doğrulama yaparken ayrıca taşınması gerekmesin.
 */
export async function signMediaToken(
  secret: string,
  claims: Omit<TokenClaims, 'exp'> & { exp?: number },
  now = Date.now()
): Promise<string> {
  const exp = claims.exp ?? Math.floor(now / 1000) + MEDIA_TOKEN_TTL_SECONDS;
  const full: TokenClaims = { ...claims, exp };
  const sig = await crypto.subtle.sign('HMAC', await keyFor(secret), enc.encode(payload(full)));
  return `${exp}.${b64url(new Uint8Array(sig))}`;
}

export type VerifyResult =
  | { ok: true; exp: number }
  | { ok: false; reason: 'malformed' | 'expired' | 'bad-signature' };

/**
 * İmzayı doğrular. Karşılaştırma sabit zamanlıdır — imzayı bayt bayt
 * tahmin etmeye çalışan bir saldırgana zamanlama ipucu vermez.
 */
export async function verifyMediaToken(
  secret: string,
  token: string,
  claims: Omit<TokenClaims, 'exp'>,
  now = Date.now()
): Promise<VerifyResult> {
  const dot = token.indexOf('.');
  if (dot <= 0) return { ok: false, reason: 'malformed' };

  const exp = Number(token.slice(0, dot));
  if (!Number.isInteger(exp)) return { ok: false, reason: 'malformed' };

  // Süre önce bakılır: süresi geçmiş bir adres için imza hesaplamaya gerek yok.
  if (exp * 1000 <= now) return { ok: false, reason: 'expired' };

  /* `Uint8Array<ArrayBuffer>` — düz `Uint8Array` TS 5.7'den beri
     `ArrayBufferLike` (yani SharedArrayBuffer da olabilir) demek ve Web Crypto
     paylaşımlı tampon kabul etmiyor. */
  let sig: Uint8Array<ArrayBuffer>;
  try {
    sig = unb64url(token.slice(dot + 1));
  } catch {
    return { ok: false, reason: 'malformed' };
  }

  const valid = await crypto.subtle.verify(
    'HMAC',
    await keyFor(secret),
    sig,
    enc.encode(payload({ ...claims, exp }))
  );

  return valid ? { ok: true, exp } : { ok: false, reason: 'bad-signature' };
}

/** Açılan kare ve komşuları için toplu imza — ok tuşları beklemesin. */
export async function signBatch(
  secret: string,
  photoIds: readonly string[],
  variant: MediaVariant,
  sid?: string,
  now = Date.now()
): Promise<{ urls: Record<string, string>; expiresAt: number }> {
  const exp = Math.floor(now / 1000) + MEDIA_TOKEN_TTL_SECONDS;
  const urls: Record<string, string> = {};
  for (const id of photoIds) {
    const t = await signMediaToken(secret, { photoId: id, variant, sid, exp }, now);
    urls[id] = `/media/${variant}/${encodeURIComponent(id)}?t=${t}`;
  }
  return { urls, expiresAt: exp };
}
